import { ELEMENT_RETRY_COUNT, findElement, pollUntil } from '../domLocator'
import humanLabel from '../humanLabel'
import { ClientTool, failure, success } from '../Tool'

interface Input extends Record<string, unknown> {
  agentId: string
  value: string
}

// Normalises an option label for fuzzy matching: lowercase, words sorted alphabetically.
// Handles LLM word-order variations like "Summer 2026" matching "2026 Summer".
function normalizeOptionText(text: string): string {
  return text.trim().toLowerCase().split(/\s+/).sort().join(' ')
}

/**
 * Ensures the dropdown is open, then clicks the option matching value inside it.
 * If the combobox is not already open (aria-expanded=false), clicks to open it.
 * Matching order: exact → case-insensitive → word-order-agnostic. Reports
 * available options if no match is found.
 */
const domSelectOption: ClientTool = {
  name: 'dom_select_option',
  category: 'mutation',
  execute: async (rawInput) => {
    const { agentId, value } = rawInput as Input
    const container = await findElement(agentId)
    if (!container) {
      return failure(`Element ${agentId} not found after ${ELEMENT_RETRY_COUNT} retries`)
    }

    const combobox = container.querySelector('[role="combobox"]') as HTMLElement | null
    if (combobox && combobox.getAttribute('aria-expanded') !== 'true') {
      combobox.click()
    }

    // Scope the listbox lookup via the combobox's aria-controls so a different
    // MUI Select open elsewhere on the page can't steal the click. Falls back
    // to the global query only when the combobox doesn't expose aria-controls
    // (rare; some custom controls don't wire it up).
    const listboxId = combobox?.getAttribute('aria-controls')
    const listbox = await pollUntil(() =>
      listboxId ? document.getElementById(listboxId) : document.querySelector('[role="listbox"]')
    )
    if (!listbox) {
      return failure(`Listbox did not appear for ${agentId}`)
    }

    const options = Array.from(listbox.querySelectorAll('[role="option"]'))
    const valueLower = value.toLowerCase()
    const valueNorm = normalizeOptionText(value)
    const match =
      options.find((o) => o.textContent?.trim() === value) ??
      options.find((o) => o.textContent?.trim().toLowerCase() === valueLower) ??
      options.find((o) => normalizeOptionText(o.textContent ?? '') === valueNorm)

    if (!match) {
      const available = options
        .map((o) => o.textContent?.trim())
        .filter(Boolean)
        .join(', ')
      return failure(`No option matching '${value}' found. Available: ${available || 'none'}`)
    }

    ;(match as HTMLElement).click()
    return success()
  },
  getStepLabel: (rawInput) => {
    const { agentId, value } = rawInput as Input
    return `Selecting "${value}" for ${humanLabel(agentId)}…`
  }
}

export default domSelectOption
